/**
 * CampanaComposerPage — Crear/editar campaña push (§8.3.2).
 * Rutas: /notificaciones/nueva y /notificaciones/:id/editar.
 *
 * Página completa: formulario en secciones numeradas a la izquierda + preview
 * sticky a la derecha (colapsa debajo en ≤1024px). Footer sticky con
 * "Guardar borrador" + botón primario contextual y autosave cada 30 s.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  FlaskConical,
  Info,
  Loader2,
  Repeat,
  Send,
  Users,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import { contarDispositivos, mensajeError } from './notificacionesApi';
import { EmojiPicker } from './EmojiPicker';
import { EnviarPruebaModal } from './EnviarPruebaModal';
import { ImagenCampanaInput } from './ImagenCampanaInput';
import { NotificationPreview } from './NotificationPreview';
import {
  botonesPayload,
  linkFinal,
  toDatetimeLocal,
  useCampanaForm,
  utmParamsDefault,
  type ModoProgramacion,
} from './useCampanaForm';
import {
  ADVERTENCIA_BOTONES,
  CATALOGO_BOTONES,
  DIAS_SEMANA,
  MAX_BOTONES,
  MAX_DESCRIPCION,
  MAX_TITULO,
  NOTA_DESCRIPCION_PUSH,
  TTL_AYUDA,
  TTL_OPCIONES,
  WARN_DESCRIPCION,
  WARN_TITULO,
  describirRecurrencia,
  imagenPreviewUrl,
  type BotonId,
  type ContenidoPrueba,
  type TtlHoras,
} from './tiposCampana';
import styles from './CampanaComposerPage.module.css';

const UMBRAL_CHECKBOX_ENTIENDO = 10_000;

export default function CampanaComposerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const {
    form,
    setField,
    errores,
    cargando,
    campanaId,
    modoEdicion,
    estadoOriginal,
    noEditableMotivo,
    guardando,
    enviando,
    ultimoGuardado,
    hayCambios,
    guardar,
    enviarAhora,
  } = useCampanaForm(id);

  const [audiencia, setAudiencia] = useState<number | null>(null);
  const [pruebaOpen, setPruebaOpen] = useState(false);
  const [confirmEnvio, setConfirmEnvio] = useState(false);
  const [entiendo, setEntiendo] = useState(false);
  const [indicadorGuardado, setIndicadorGuardado] = useState<string | null>(null);
  const [limitesProgramada] = useState(() => ({
    min: toDatetimeLocal(new Date(Date.now() + 5 * 60_000)),
    max: toDatetimeLocal(new Date(Date.now() + 30 * 24 * 3_600_000)),
  }));

  // Conteo de audiencia (cacheado en memoria 5 min)
  useEffect(() => {
    let activo = true;
    contarDispositivos()
      .then((total) => {
        if (activo) setAudiencia(total);
      })
      .catch(() => {
        if (activo) setAudiencia(null);
      });
    return () => {
      activo = false;
    };
  }, []);

  // Indicador "Guardado hace Xs" (recalculado cada 5 s fuera del render)
  useEffect(() => {
    const calcular = () => {
      if (!ultimoGuardado) {
        setIndicadorGuardado(hayCambios ? 'Cambios sin guardar' : null);
        return;
      }
      const seg = Math.max(0, Math.round((Date.now() - ultimoGuardado) / 1000));
      const texto =
        seg < 60 ? `Guardado hace ${seg}s` : `Guardado hace ${Math.floor(seg / 60)} min`;
      setIndicadorGuardado(hayCambios ? `${texto} · cambios sin guardar` : texto);
    };
    calcular();
    const t = setInterval(calcular, 5_000);
    return () => clearInterval(t);
  }, [ultimoGuardado, hayCambios]);

  // ─── Contadores ────────────────────────────────────────────
  const tituloWarn = form.titulo.length > WARN_TITULO;
  const descWarn = form.descripcion.length > WARN_DESCRIPCION;

  // ─── Preview / prueba ──────────────────────────────────────
  const previewContenido = useMemo(
    () => ({
      titulo: form.titulo,
      descripcion: form.descripcion,
      imagenUrl: imagenPreviewUrl(form.imagen),
      botones: form.botones.map((b) => b.id),
    }),
    [form.titulo, form.descripcion, form.imagen, form.botones],
  );

  const contenidoPrueba = useMemo<ContenidoPrueba>(() => {
    const contenido: ContenidoPrueba = {
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim(),
      prioridad: form.prioridad,
    };
    // La imagen local (blob) aún no está en Storage; solo URLs ya resueltas
    if (form.imagen && form.imagen.tipo !== 'blob') {
      contenido.imagenUrl = form.imagen.url;
    }
    const link = linkFinal(form);
    if (link) contenido.linkMasInfo = link;
    const botones = botonesPayload(form);
    if (botones) contenido.botones = botones;
    return contenido;
  }, [form]);

  // ─── Botones de acción ─────────────────────────────────────
  const toggleBoton = (botonId: BotonId) => {
    const seleccionado = form.botones.some((b) => b.id === botonId);
    if (seleccionado) {
      setField('botones', form.botones.filter((b) => b.id !== botonId));
      return;
    }
    if (form.botones.length >= MAX_BOTONES) return;
    const item = CATALOGO_BOTONES.find((c) => c.id === botonId);
    setField('botones', [...form.botones, { id: botonId, valor: item?.valorDefault ?? '' }]);
  };

  const setValorBoton = (botonId: BotonId, valor: string) => {
    setField(
      'botones',
      form.botones.map((b) => (b.id === botonId ? { ...b, valor } : b)),
    );
  };

  // ─── UTM ───────────────────────────────────────────────────
  const toggleUtm = (activo: boolean) => {
    setField('utmActivo', activo);
    if (activo && !form.utmParams.trim()) {
      setField('utmParams', utmParamsDefault(form.nombre, campanaId));
    }
  };

  // ─── Acciones de guardado ──────────────────────────────────
  const irAEditarSiNuevo = (nuevoId: string) => {
    if (!modoEdicion) {
      navigate(`/notificaciones/${nuevoId}/editar`, { replace: true });
    }
  };

  const onGuardarBorrador = async () => {
    try {
      const nuevoId = await guardar('borrador');
      if (!nuevoId) {
        addToast('error', 'Revise los campos marcados antes de guardar.');
        return;
      }
      addToast('success', 'Borrador guardado.');
      irAEditarSiNuevo(nuevoId);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo guardar el borrador.'));
    }
  };

  const onProgramar = async () => {
    try {
      const nuevoId = await guardar('programar');
      if (!nuevoId) {
        addToast('error', 'Revise los campos marcados antes de programar.');
        return;
      }
      addToast('success', 'Campaña programada correctamente.');
      navigate('/notificaciones');
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo programar la campaña.'));
    }
  };

  const onActivarRecurrencia = async () => {
    try {
      const nuevoId = await guardar('recurrencia');
      if (!nuevoId) {
        addToast('error', 'Revise los campos marcados antes de activar la recurrencia.');
        return;
      }
      addToast('success', 'Recurrencia activada.');
      navigate('/notificaciones');
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo activar la recurrencia.'));
    }
  };

  const onConfirmarEnvio = async () => {
    try {
      const res = await enviarAhora();
      if (!res) {
        setConfirmEnvio(false);
        addToast('error', 'Revise los campos marcados antes de enviar.');
        return;
      }
      addToast(
        'success',
        'Envío iniciado. El progreso se actualiza en vivo en el detalle de la campaña.',
      );
      navigate(`/notificaciones/${res.campanaId}`);
    } catch (err) {
      setConfirmEnvio(false);
      addToast('error', mensajeError(err, 'No se pudo enviar la campaña.'));
    }
  };

  const abrirConfirmEnvio = () => {
    setEntiendo(false);
    setConfirmEnvio(true);
  };

  const accionPrimaria = () => {
    if (form.programacion === 'programada') void onProgramar();
    else if (form.programacion === 'recurrente') void onActivarRecurrencia();
    else abrirConfirmEnvio();
  };

  const labelPrimario =
    form.programacion === 'programada'
      ? 'Programar campaña'
      : form.programacion === 'recurrente'
        ? 'Activar recurrencia'
        : 'Enviar ahora';

  const iconoPrimario =
    form.programacion === 'programada' ? (
      <CalendarClock size={15} aria-hidden="true" />
    ) : form.programacion === 'recurrente' ? (
      <Repeat size={15} aria-hidden="true" />
    ) : (
      <Send size={15} aria-hidden="true" />
    );

  const requiereEntiendo = (audiencia ?? 0) > UMBRAL_CHECKBOX_ENTIENDO;

  // ─── Resumen de programación (④) ───────────────────────────
  const resumenProgramacion = useMemo(() => {
    if (form.programacion === 'programada') {
      return form.programadaPara
        ? `Programada para ${new Intl.DateTimeFormat('es-PA', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).format(new Date(form.programadaPara))} (hora de Panamá)`
        : 'Programada — seleccione fecha y hora';
    }
    if (form.programacion === 'recurrente') {
      return describirRecurrencia({
        tipo: form.recTipo,
        diaSemana: form.recDiaSemana,
        diaMes: form.recDiaMes,
        hora: form.recHora,
      });
    }
    return 'Envío inmediato al confirmar';
  }, [form]);

  // ─── Render ────────────────────────────────────────────────

  if (noEditableMotivo) {
    return (
      <MainLayout pageTitle="Editar campaña" onBack={() => navigate('/notificaciones')}>
        <div className={styles.page}>
          <Card className={styles.noEditable}>
            <AlertTriangle size={20} aria-hidden="true" />
            <p>{noEditableMotivo}</p>
            <Button variant="secondary" onClick={() => navigate('/notificaciones')}>
              Volver a campañas
            </Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (cargando) {
    return (
      <MainLayout pageTitle="Editar campaña" onBack={() => navigate('/notificaciones')}>
        <div className={styles.page}>
          <p className={styles.cargando}>
            <Loader2 size={16} className={styles.spin} aria-hidden="true" /> Cargando campaña…
          </p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      pageTitle={modoEdicion ? 'Editar campaña' : 'Nueva campaña'}
      pageSubtitle={
        modoEdicion && estadoOriginal
          ? `Editando una campaña en estado "${estadoOriginal}"`
          : 'Componga la notificación, defina la audiencia y programe el envío'
      }
      onBack={() => navigate('/notificaciones')}
    >
      <div className={styles.page}>
        <div className={styles.grid}>
          {/* ════════ Columna del formulario ════════ */}
          <div className={styles.formCol}>
            {/* ─── ① Contenido ─── */}
            <Card className={styles.seccion}>
              <div className={styles.seccionHeader}>
                <span className={styles.seccionNum} aria-hidden="true">1</span>
                <h2 className={styles.seccionTitulo}>Contenido</h2>
                <Button
                  variant="secondary"
                  size="sm"
                  className={styles.pruebaBtn}
                  disabled={!form.titulo.trim() || !form.descripcion.trim()}
                  onClick={() => setPruebaOpen(true)}
                >
                  <FlaskConical size={14} aria-hidden="true" /> Enviar prueba
                </Button>
              </div>

              <Input
                label="Nombre interno de la campaña"
                placeholder="Ej. Jornada de vacunación julio"
                value={form.nombre}
                onChange={(e) => setField('nombre', e.target.value)}
                error={errores.nombre}
              />
              <p className={styles.hint}>
                No se envía a los usuarios; identifica la campaña en la lista y los reportes.
              </p>

              {/* Título con contador + emoji */}
              <div className={styles.campoContado}>
                <div className={styles.campoContadoInput}>
                  <Input
                    label="Título de la notificación"
                    placeholder="Ej. 💉 Vacúnese gratis este sábado"
                    value={form.titulo}
                    maxLength={MAX_TITULO}
                    onChange={(e) => setField('titulo', e.target.value)}
                    error={errores.titulo}
                  />
                  <div className={styles.emojiSlot}>
                    <EmojiPicker
                      label="Insertar emoji en el título"
                      onSelect={(emoji) =>
                        setField('titulo', `${form.titulo}${emoji}`.slice(0, MAX_TITULO))
                      }
                    />
                  </div>
                </div>
                <div className={styles.contadorRow}>
                  <span
                    className={`${styles.contador} ${tituloWarn ? styles.contadorWarn : ''}`}
                    aria-live="polite"
                  >
                    {form.titulo.length}/{MAX_TITULO}
                  </span>
                  {tituloWarn && (
                    <span className={styles.contadorLeyenda}>
                      iOS puede truncar después de ~50 caracteres
                    </span>
                  )}
                </div>
              </div>

              {/* Descripción con contador + emoji */}
              <div className={styles.campoContado}>
                <div className={styles.campoContadoInput}>
                  <div className={styles.textareaWrap}>
                    <label htmlFor="campana-descripcion" className={styles.label}>
                      Descripción
                    </label>
                    <textarea
                      id="campana-descripcion"
                      className={`${styles.textarea} ${errores.descripcion ? styles.textareaError : ''}`}
                      placeholder="Texto del cuerpo de la notificación…"
                      rows={3}
                      maxLength={MAX_DESCRIPCION}
                      value={form.descripcion}
                      onChange={(e) => setField('descripcion', e.target.value)}
                      aria-invalid={!!errores.descripcion || undefined}
                      aria-describedby={
                        errores.descripcion ? 'campana-descripcion-error' : undefined
                      }
                    />
                    {errores.descripcion && (
                      <p
                        id="campana-descripcion-error"
                        className={styles.errorMsg}
                        role="alert"
                      >
                        {errores.descripcion}
                      </p>
                    )}
                  </div>
                  <div className={styles.emojiSlot}>
                    <EmojiPicker
                      label="Insertar emoji en la descripción"
                      onSelect={(emoji) =>
                        setField(
                          'descripcion',
                          `${form.descripcion}${emoji}`.slice(0, MAX_DESCRIPCION),
                        )
                      }
                    />
                  </div>
                </div>
                <div className={styles.contadorRow}>
                  <span
                    className={`${styles.contador} ${descWarn ? styles.contadorWarn : ''}`}
                    aria-live="polite"
                  >
                    {form.descripcion.length}/{MAX_DESCRIPCION}
                  </span>
                  {descWarn && (
                    <span className={styles.contadorLeyenda}>
                      La notificación se cortará; el texto completo se verá en la app
                    </span>
                  )}
                </div>
                <p className={styles.notaDescripcion}>
                  <Info size={13} aria-hidden="true" />
                  {NOTA_DESCRIPCION_PUSH}
                </p>
              </div>

              {/* Imagen */}
              <div className={styles.subseccion}>
                <h3 className={styles.subseccionTitulo}>Imagen (opcional)</h3>
                <ImagenCampanaInput
                  imagen={form.imagen}
                  onChange={(img) => setField('imagen', img)}
                />
              </div>

              {/* Link más información */}
              <div className={styles.subseccion}>
                <Input
                  label='Link "más información" (opcional)'
                  placeholder="https://minsa.gob.pa/..."
                  value={form.linkMasInfo}
                  onChange={(e) => setField('linkMasInfo', e.target.value)}
                  error={errores.linkMasInfo}
                  inputMode="url"
                />
                <label className={styles.toggleRow}>
                  <input
                    type="checkbox"
                    checked={form.utmActivo}
                    onChange={(e) => toggleUtm(e.target.checked)}
                  />
                  <span>Agregar parámetros UTM al link</span>
                </label>
                {form.utmActivo && (
                  <Input
                    label="Parámetros UTM"
                    value={form.utmParams}
                    onChange={(e) => setField('utmParams', e.target.value)}
                  />
                )}
              </div>

              {/* Botones de acción */}
              <div className={styles.subseccion}>
                <h3 className={styles.subseccionTitulo}>
                  Botones de acción (máx {MAX_BOTONES})
                </h3>
                <div className={styles.chips} role="group" aria-label="Botones de acción">
                  {CATALOGO_BOTONES.map((item) => {
                    const activo = form.botones.some((b) => b.id === item.id);
                    const deshabilitado = !activo && form.botones.length >= MAX_BOTONES;
                    return (
                      <label
                        key={item.id}
                        className={`${styles.chip} ${activo ? styles.chipActivo : ''} ${deshabilitado ? styles.chipDeshabilitado : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={activo}
                          disabled={deshabilitado}
                          onChange={() => toggleBoton(item.id)}
                        />
                        {item.label}
                      </label>
                    );
                  })}
                </div>
                {form.botones.map((b) => {
                  const item = CATALOGO_BOTONES.find((c) => c.id === b.id);
                  return (
                    <Input
                      key={b.id}
                      label={`Acción de "${item?.label ?? b.id}"`}
                      placeholder={item?.hint}
                      type={b.id === 'llamar' ? 'tel' : 'text'}
                      inputMode={b.id === 'llamar' ? 'tel' : b.id === 'mas_info' ? 'url' : undefined}
                      value={b.valor}
                      onChange={(e) => setValorBoton(b.id, e.target.value)}
                      error={errores[`boton_${b.id}`]}
                    />
                  );
                })}
                {form.botones.length > 0 && (
                  <p className={styles.advertencia}>
                    <AlertTriangle size={13} aria-hidden="true" /> {ADVERTENCIA_BOTONES}
                  </p>
                )}
              </div>
            </Card>

            {/* ─── ② Audiencia ─── */}
            <Card className={styles.seccion}>
              <div className={styles.seccionHeader}>
                <span className={styles.seccionNum} aria-hidden="true">2</span>
                <h2 className={styles.seccionTitulo}>Audiencia</h2>
              </div>

              <div className={styles.audienciaCard}>
                <Users size={20} aria-hidden="true" />
                <div>
                  <p className={styles.audienciaTitulo}>Todos los usuarios de la app</p>
                  <p className={styles.audienciaConteo}>
                    {audiencia === null
                      ? 'Calculando dispositivos registrados…'
                      : `~${audiencia.toLocaleString('es-PA')} dispositivos registrados`}
                  </p>
                </div>
              </div>

              <div className={styles.segmentos}>
                {['Por región', 'Por rango de edad', 'Usuarios específicos'].map((seg) => (
                  <label key={seg} className={styles.segmentoDeshabilitado}>
                    <input type="radio" name="segmento" disabled />
                    <span>{seg}</span>
                    <Badge variant="neutral" size="sm">Próximamente</Badge>
                  </label>
                ))}
              </div>
            </Card>

            {/* ─── ③ Programación y vigencia ─── */}
            <Card className={styles.seccion}>
              <div className={styles.seccionHeader}>
                <span className={styles.seccionNum} aria-hidden="true">3</span>
                <h2 className={styles.seccionTitulo}>Programación y vigencia</h2>
              </div>

              <div className={styles.radios} role="radiogroup" aria-label="Modo de envío">
                {(
                  [
                    { value: 'ahora', label: 'Enviar ahora' },
                    { value: 'programada', label: 'Programada' },
                    { value: 'recurrente', label: 'Recurrente' },
                  ] as { value: ModoProgramacion; label: string }[]
                ).map((op) => (
                  <label
                    key={op.value}
                    className={`${styles.radio} ${form.programacion === op.value ? styles.radioActivo : ''}`}
                  >
                    <input
                      type="radio"
                      name="programacion"
                      value={op.value}
                      checked={form.programacion === op.value}
                      onChange={() => setField('programacion', op.value)}
                    />
                    <span>{op.label}</span>
                  </label>
                ))}
              </div>

              {form.programacion === 'programada' && (
                <div className={styles.panelProgramacion}>
                  <label htmlFor="campana-programada" className={styles.label}>
                    Fecha y hora de envío
                  </label>
                  <input
                    id="campana-programada"
                    type="datetime-local"
                    className={`${styles.inputNativo} ${errores.programadaPara ? styles.inputNativoError : ''}`}
                    value={form.programadaPara}
                    min={limitesProgramada.min}
                    max={limitesProgramada.max}
                    onChange={(e) => setField('programadaPara', e.target.value)}
                    aria-invalid={!!errores.programadaPara || undefined}
                  />
                  {errores.programadaPara && (
                    <p className={styles.errorMsg} role="alert">{errores.programadaPara}</p>
                  )}
                  <p className={styles.hint}>Hora de Panamá (GMT-5)</p>
                </div>
              )}

              {form.programacion === 'recurrente' && (
                <div className={styles.panelProgramacion}>
                  <div className={styles.filaCampos}>
                    <div className={styles.campoSelect}>
                      <label htmlFor="rec-tipo" className={styles.label}>Frecuencia</label>
                      <select
                        id="rec-tipo"
                        className={styles.inputNativo}
                        value={form.recTipo}
                        onChange={(e) =>
                          setField('recTipo', e.target.value as typeof form.recTipo)
                        }
                      >
                        <option value="diaria">Diaria</option>
                        <option value="semanal">Semanal</option>
                        <option value="mensual">Mensual</option>
                      </select>
                    </div>

                    {form.recTipo === 'semanal' && (
                      <div className={styles.campoSelect}>
                        <label htmlFor="rec-dia-semana" className={styles.label}>
                          Día de la semana
                        </label>
                        <select
                          id="rec-dia-semana"
                          className={styles.inputNativo}
                          value={form.recDiaSemana}
                          onChange={(e) => setField('recDiaSemana', Number(e.target.value))}
                        >
                          {DIAS_SEMANA.map((d) => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {form.recTipo === 'mensual' && (
                      <div className={styles.campoSelect}>
                        <label htmlFor="rec-dia-mes" className={styles.label}>Día del mes</label>
                        <select
                          id="rec-dia-mes"
                          className={styles.inputNativo}
                          value={form.recDiaMes}
                          onChange={(e) => setField('recDiaMes', Number(e.target.value))}
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className={styles.campoSelect}>
                      <label htmlFor="rec-hora" className={styles.label}>Hora</label>
                      <input
                        id="rec-hora"
                        type="time"
                        className={styles.inputNativo}
                        value={form.recHora}
                        onChange={(e) => setField('recHora', e.target.value)}
                      />
                    </div>
                  </div>
                  <p className={styles.hint}>Hora de Panamá (GMT-5)</p>

                  <fieldset className={styles.finFieldset}>
                    <legend className={styles.label}>Finaliza</legend>
                    <div className={styles.radios}>
                      <label
                        className={`${styles.radio} ${form.recFin === 'nunca' ? styles.radioActivo : ''}`}
                      >
                        <input
                          type="radio"
                          name="recFin"
                          checked={form.recFin === 'nunca'}
                          onChange={() => setField('recFin', 'nunca')}
                        />
                        <span>Nunca</span>
                      </label>
                      <label
                        className={`${styles.radio} ${form.recFin === 'fecha' ? styles.radioActivo : ''}`}
                      >
                        <input
                          type="radio"
                          name="recFin"
                          checked={form.recFin === 'fecha'}
                          onChange={() => setField('recFin', 'fecha')}
                        />
                        <span>En fecha</span>
                      </label>
                      <label
                        className={`${styles.radio} ${form.recFin === 'envios' ? styles.radioActivo : ''}`}
                      >
                        <input
                          type="radio"
                          name="recFin"
                          checked={form.recFin === 'envios'}
                          onChange={() => setField('recFin', 'envios')}
                        />
                        <span>Después de N envíos</span>
                      </label>
                    </div>

                    {form.recFin === 'fecha' && (
                      <div className={styles.campoSelect}>
                        <label htmlFor="rec-fin-fecha" className={styles.label}>
                          Fecha de finalización
                        </label>
                        <input
                          id="rec-fin-fecha"
                          type="date"
                          className={`${styles.inputNativo} ${errores.recFinFecha ? styles.inputNativoError : ''}`}
                          value={form.recFinFecha}
                          onChange={(e) => setField('recFinFecha', e.target.value)}
                        />
                        {errores.recFinFecha && (
                          <p className={styles.errorMsg} role="alert">{errores.recFinFecha}</p>
                        )}
                      </div>
                    )}

                    {form.recFin === 'envios' && (
                      <div className={styles.campoSelect}>
                        <label htmlFor="rec-max-envios" className={styles.label}>
                          Número de envíos
                        </label>
                        <input
                          id="rec-max-envios"
                          type="number"
                          min={1}
                          className={`${styles.inputNativo} ${errores.recMaxEnvios ? styles.inputNativoError : ''}`}
                          value={form.recMaxEnvios}
                          onChange={(e) => setField('recMaxEnvios', e.target.value)}
                        />
                        {errores.recMaxEnvios && (
                          <p className={styles.errorMsg} role="alert">{errores.recMaxEnvios}</p>
                        )}
                      </div>
                    )}
                  </fieldset>
                </div>
              )}

              <div className={styles.filaCampos}>
                <div className={styles.campoSelect}>
                  <label htmlFor="campana-ttl" className={styles.label}>Vigencia</label>
                  <select
                    id="campana-ttl"
                    className={styles.inputNativo}
                    value={form.ttlHoras}
                    onChange={(e) => setField('ttlHoras', Number(e.target.value) as TtlHoras)}
                  >
                    {TTL_OPCIONES.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className={styles.hint}>{TTL_AYUDA}</p>

              <div className={styles.radios} role="radiogroup" aria-label="Prioridad">
                <label
                  className={`${styles.radio} ${form.prioridad === 'normal' ? styles.radioActivo : ''}`}
                >
                  <input
                    type="radio"
                    name="prioridad"
                    checked={form.prioridad === 'normal'}
                    onChange={() => setField('prioridad', 'normal')}
                  />
                  <span>Prioridad normal</span>
                </label>
                <label
                  className={`${styles.radio} ${form.prioridad === 'alta' ? styles.radioActivo : ''}`}
                >
                  <input
                    type="radio"
                    name="prioridad"
                    checked={form.prioridad === 'alta'}
                    onChange={() => setField('prioridad', 'alta')}
                  />
                  <span>Prioridad alta</span>
                </label>
              </div>
              <p className={styles.hint}>Alta solo para avisos urgentes de salud.</p>
            </Card>

            {/* ─── ④ Revisión y envío ─── */}
            <Card className={styles.seccion}>
              <div className={styles.seccionHeader}>
                <span className={styles.seccionNum} aria-hidden="true">4</span>
                <h2 className={styles.seccionTitulo}>Revisión y envío</h2>
              </div>

              <dl className={styles.resumen}>
                <div className={styles.resumenFila}>
                  <dt>Contenido</dt>
                  <dd>
                    <div className={styles.resumenContenido}>
                      {imagenPreviewUrl(form.imagen) && (
                        <img
                          src={imagenPreviewUrl(form.imagen) ?? ''}
                          alt=""
                          className={styles.resumenThumb}
                        />
                      )}
                      <div>
                        <p className={styles.resumenTitulo}>{form.titulo || 'Sin título'}</p>
                        <p className={styles.resumenDesc}>
                          {form.descripcion || 'Sin descripción'}
                        </p>
                      </div>
                    </div>
                  </dd>
                </div>
                <div className={styles.resumenFila}>
                  <dt>Audiencia</dt>
                  <dd>
                    {audiencia === null
                      ? 'Todos los usuarios de la app'
                      : `Se enviará a ~${audiencia.toLocaleString('es-PA')} dispositivos`}
                  </dd>
                </div>
                <div className={styles.resumenFila}>
                  <dt>Programación</dt>
                  <dd>{resumenProgramacion}</dd>
                </div>
                <div className={styles.resumenFila}>
                  <dt>Vigencia</dt>
                  <dd>
                    {TTL_OPCIONES.find((o) => o.value === form.ttlHoras)?.label} · prioridad{' '}
                    {form.prioridad}
                  </dd>
                </div>
                <div className={styles.resumenFila}>
                  <dt>Botones</dt>
                  <dd>
                    {form.botones.length === 0
                      ? 'Sin botones de acción'
                      : form.botones
                          .map(
                            (b) => CATALOGO_BOTONES.find((c) => c.id === b.id)?.label ?? b.id,
                          )
                          .join(' · ')}
                  </dd>
                </div>
                {form.linkMasInfo.trim() && (
                  <div className={styles.resumenFila}>
                    <dt>Link</dt>
                    <dd className={styles.resumenLink}>{linkFinal(form)}</dd>
                  </div>
                )}
              </dl>
            </Card>
          </div>

          {/* ════════ Preview sticky ════════ */}
          <aside className={styles.previewCol} aria-label="Vista previa de la notificación">
            <div className={styles.previewSticky}>
              <h2 className={styles.previewTitulo}>Vista previa</h2>
              <NotificationPreview contenido={previewContenido} />
            </div>
          </aside>
        </div>

        {/* ════════ Footer sticky ════════ */}
        <div className={styles.footer}>
          <span className={styles.autosave} aria-live="polite">
            {indicadorGuardado}
          </span>
          <div className={styles.footerAcciones}>
            <Button
              variant="secondary"
              loading={guardando && !enviando}
              onClick={() => void onGuardarBorrador()}
            >
              Guardar borrador
            </Button>
            <Button
              variant="primary"
              loading={enviando || (guardando && form.programacion !== 'ahora')}
              onClick={accionPrimaria}
            >
              {iconoPrimario} {labelPrimario}
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Modal enviar prueba ─── */}
      <EnviarPruebaModal
        open={pruebaOpen}
        onClose={() => setPruebaOpen(false)}
        contenido={contenidoPrueba}
      />

      {/* ─── Modal confirmar envío inmediato ─── */}
      <Modal open={confirmEnvio} onClose={() => setConfirmEnvio(false)} title="Confirmar envío">
        <div className={styles.confirmBody}>
          <p className={styles.confirmTexto}>
            Vas a enviar esta campaña a ~
            {audiencia !== null ? audiencia.toLocaleString('es-PA') : '…'} dispositivos. Esta
            acción no se puede cancelar una vez iniciada.
          </p>
          {requiereEntiendo && (
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={entiendo}
                onChange={(e) => setEntiendo(e.target.checked)}
              />
              <span>Entiendo que el envío es masivo e irreversible</span>
            </label>
          )}
          <div className={styles.confirmAcciones}>
            <Button variant="secondary" onClick={() => setConfirmEnvio(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={enviando || guardando}
              disabled={requiereEntiendo && !entiendo}
              onClick={() => void onConfirmarEnvio()}
            >
              <Send size={14} aria-hidden="true" /> Enviar campaña
            </Button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
